/**
 * Georgian-safe date formatting.
 *
 * Why not Intl with 'ka-GE': not every browser ships Georgian ICU data —
 * headless Chromium and some Android WebViews silently resolve 'ka-GE'
 * to en-US, which puts English month names on the SUBSCRIPTION (money)
 * screen for exactly the users we built it for. Month names are a fixed
 * list of twelve; we own them. English falls back to Intl.
 */

const KA_MONTHS = [
  'იანვარი',
  'თებერვალი',
  'მარტი',
  'აპრილი',
  'მაისი',
  'ივნისი',
  'ივლისი',
  'აგვისტო',
  'სექტემბერი',
  'ოქტომბერი',
  'ნოემბერი',
  'დეკემბერი',
] as const;

const KA_MONTHS_SHORT = [
  'იან',
  'თებ',
  'მარ',
  'აპრ',
  'მაი',
  'ივნ',
  'ივლ',
  'აგვ',
  'სექ',
  'ოქტ',
  'ნოე',
  'დეკ',
] as const;

export type DateLang = 'ka' | 'en';

export function toDateLang(i18nLanguage: string): DateLang {
  return i18nLanguage.startsWith('ka') ? 'ka' : 'en';
}

/** "16 აგვისტო, 2026" / "August 16, 2026". '—' for null/invalid. */
export function formatDateLong(iso: string | null | undefined, lang: DateLang): string {
  const d = parse(iso);
  if (!d) return '—';
  if (lang === 'ka') return `${d.getDate()} ${KA_MONTHS[d.getMonth()]}, ${d.getFullYear()}`;
  return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long', day: 'numeric' }).format(d);
}

/** "16 აგვისტო" / "August 16" — for compact status lines. */
export function formatDateDayMonth(iso: string | null | undefined, lang: DateLang): string {
  const d = parse(iso);
  if (!d) return '—';
  if (lang === 'ka') return `${d.getDate()} ${KA_MONTHS[d.getMonth()]}`;
  return new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric' }).format(d);
}

/** "16 აგვ 2026" / "Aug 16, 2026" — for history rows. */
export function formatDateShort(iso: string | null | undefined, lang: DateLang): string {
  const d = parse(iso);
  if (!d) return '—';
  if (lang === 'ka') return `${d.getDate()} ${KA_MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
  return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' }).format(d);
}

/** "აპრილი 2026" / "April 2026". */
export function formatMonthYear(iso: string | null | undefined, lang: DateLang): string {
  const d = parse(iso);
  if (!d) return '—';
  if (lang === 'ka') return `${KA_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long' }).format(d);
}

/** Ablative ("since …") month forms — stems change (თებერვალი → თებერვლიდან),
 *  so these are spelled out rather than suffixed mechanically. */
const KA_MONTHS_FROM = [
  'იანვრიდან',
  'თებერვლიდან',
  'მარტიდან',
  'აპრილიდან',
  'მაისიდან',
  'ივნისიდან',
  'ივლისიდან',
  'აგვისტოდან',
  'სექტემბრიდან',
  'ოქტომბრიდან',
  'ნოემბრიდან',
  'დეკემბრიდან',
] as const;

/** Member-since: "2026 წლის აპრილიდან" / "April 2026" — the ka form is the
 *  native construction, not a calqued "member April 2026-dan". */
export function formatMemberSince(iso: string | null | undefined, lang: DateLang): string {
  const d = parse(iso);
  if (!d) return '—';
  if (lang === 'ka') return `${d.getFullYear()} წლის ${KA_MONTHS_FROM[d.getMonth()]}`;
  return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long' }).format(d);
}

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}
